import { useQuery } from "@tanstack/react-query";
import { Redirect, useLocation, useSearch } from "wouter";
import { usePermissions, ViewOnlyBanner } from "@/hooks/use-permissions";
import AppLayout  from "@/components/app-layout";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { useEffect } from "react";
import { useBetaAccessState } from "@/hooks/use-beta-access";
import { useFeatureGates, gateBlockForKey, gateBlockForRoute } from "@/hooks/use-feature-gates";
import { ComingSoon } from "@/components/coming-soon";
import type { Property } from "@shared/schema";
import { BackToPropertyLink } from "@/components/back-to-property-link";
import { usePageTitle } from "@/hooks/use-page-title";
import { ProfitAndLoss } from "./app-financials/ProfitAndLossTab";
import { BalanceTab } from "./app-financials/BalanceTab";
import { useMercuryEligibility } from "./app-financials/use-mercury-eligibility";
import { ExpensesTab } from "./app-financials/ExpensesTab";
import { SettingsTab } from "./app-financials/SettingsTab";
import { IncomeTab } from "./app-financials/IncomeTab";
import { ForwardedDocsTab } from "./app-financials/ForwardedDocsTab";
import { StatementsTab } from "./app-financials/StatementsTab";
import { CardsTab } from "./app-financials/CardsTab";
export { MercuryBankCard } from "./app-financials/SettingsTab";

type FinTab = "balance" | "pnl" | "expenses" | "settings" | "mail-inbox" | "statements" | "income" | "cards";

const TAB_ROUTES: Record<FinTab, string> = {
  balance: "/app/financials/balance",
  pnl: "/app/financials",
  expenses: "/app/financials/expenses",
  settings: "/app/financials/settings",
  "mail-inbox": "/app/financials/mail-inbox",
  statements: "/app/financials/statements",
  income: "/app/financials/income",
  cards: "/app/financials/cards",
};

function deriveTab(loc: string): FinTab {
  if (loc.includes("/balance")) return "balance";
  if (loc.includes("/expenses")) return "expenses";
  if (loc.includes("/settings")) return "settings";
  if (loc.includes("/mail-inbox")) return "mail-inbox";
  if (loc.includes("/statements")) return "statements";
  if (loc.includes("/income")) return "income";
  if (loc.includes("/cards")) return "cards";
  return "pnl";
}

export default function AppFinancials() {
  const [location] = useLocation();
  const search = useSearch();
  if (location === "/app/financials/recurring" || location === "/app/financials/recurring/") {
    return <Redirect to={`/app/financials/expenses${search ? `?${search}` : ""}`} replace />;
  }
  return (
    <AppLayout>
      <FinancialsContent />
    </AppLayout>
  );
}

// Keep page and tab permission consumers inside the layout's provider.
function FinancialsContent() {
  usePageTitle("Financials");
  const [location, setLocation] = useLocation();
  const search = useSearch();
  const activeTab = deriveTab(location);
  const { canEdit: canEditArea } = usePermissions();
  const readOnly = !canEditArea("financials");
  const mercuryEligibility = useMercuryEligibility();

  const searchParams = new URLSearchParams(search);
  const initialPropertyId = searchParams.get("propertyId") || undefined;

  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const properties = propertiesQuery.data ?? [];

  // Private Forwarded Docs routes are guarded before this page mounts.
  // The tab and its queries also fail closed unless this user's grant is active.
  const { granted: hasForwardedDocsBeta } = useBetaAccessState("forwarded_docs");
  // Task #3898 — Cards hub tab: visible to beta-granted users with the card
  // provisioning beta + Growth tier (both gated on the backend as well).
  const { granted: hasCardsBeta } = useBetaAccessState("mercury_card_provisioning");

  const pendingMailCount = useQuery<{ count: number }>({
    queryKey: ["/api/app/mail-documents/pending-count"],
    refetchInterval: 5 * 60 * 1000,
    enabled: hasForwardedDocsBeta,
  });
  const mailPending = pendingMailCount.data?.count ?? 0;

  const { byKey: gatesByKey, loaded: gatesLoaded, failed: gatesFailed } = useFeatureGates();
  const pnlGate = gateBlockForKey(gatesByKey, "revenue_pnl");
  const mercuryConnQuery = useQuery<{ status?: string } | null>({
    queryKey: ["/api/app/mercury-connection"],
    enabled: mercuryEligibility.allowed && gatesLoaded && !gatesFailed &&
      !gateBlockForRoute(gatesByKey, location) && (activeTab !== "balance" || !pnlGate),
  });
  const mercuryConnected = !!mercuryConnQuery.data && mercuryConnQuery.data?.status !== "disconnected";
  const showStatementsTab = mercuryConnected;
  const showCardsTab = hasCardsBeta && mercuryConnected;

  // Task #3976 — admin "Coming Soon"/"Hidden" gating for individual Revenue
  // sub-tabs. The whole Revenue *section* gate is handled upstream by the
  // app-layout route overlay (it replaces this page entirely), so here we only
  // resolve the per-tab gates by key. Exempt users (admin/granted) get null.
  const incomeGate = gateBlockForKey(gatesByKey, "revenue_income");
  const statementsGate = gateBlockForKey(gatesByKey, "revenue_statements");
  const cardsGate = gateBlockForKey(gatesByKey, "revenue_cards");
  const tabHiddenByKey: Partial<Record<FinTab, boolean>> = {
    // Balance inherits the former panel's P&L gate; no new grant policy.
    balance: pnlGate?.mode === "hidden",
    pnl: pnlGate?.mode === "hidden",
    income: incomeGate?.mode === "hidden",
    statements: statementsGate?.mode === "hidden",
    cards: cardsGate?.mode === "hidden",
  };

  function navigateTab(tab: FinTab) {
    const qs = search ? (search.startsWith("?") ? search : `?${search}`) : "";
    setLocation(TAB_ROUTES[tab] + qs);
  }

  const allTabs: { key: FinTab; label: string; badge?: number }[] = [
    { key: "balance", label: "Balance" },
    { key: "pnl", label: "Profit & Loss" },
    { key: "income", label: "Income" },
    { key: "expenses", label: "Expenses" },
    ...(hasForwardedDocsBeta ? [{ key: "mail-inbox" as FinTab, label: "Forwarded Docs", badge: mailPending > 0 ? mailPending : undefined }] : []),
    ...(showStatementsTab ? [{ key: "statements" as FinTab, label: "Statements" }] : []),
    ...(showCardsTab ? [{ key: "cards" as FinTab, label: "Cards" }] : []),
    { key: "settings", label: "Settings" },
  ];
  const tabs = allTabs.filter((t) => {
    if (tabHiddenByKey[t.key] === true) return false;
    // Task #4054 — when the whole Revenue *section* is gated and the user only
    // holds a sub-tab grant (e.g. Cards), hide the tabs the section gate would
    // still block so the bar shows only what they can actually open — no dead-
    // click that bounces them onto the gate overlay. gateBlockForRoute applies
    // the SAME path-scoped sub-tab→section exemption the route overlay uses, so
    // admins / fully-exempt users (and the ungated case) get null for every tab
    // and keep seeing the full bar.
    if (gateBlockForRoute(gatesByKey, TAB_ROUTES[t.key])) return false;
    return true;
  });

  // If the active tab was just hidden by an admin gate, fall back to the first
  // still-visible tab (the URL follows so a hard refresh also lands correctly).
  useEffect(() => {
    if (!gatesLoaded || tabs.some((t) => t.key === activeTab)) return;
    const fallback = tabs[0]?.key ?? "settings";
    navigateTab(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatesLoaded, activeTab, pnlGate?.mode, incomeGate?.mode, statementsGate?.mode, cardsGate?.mode]);

  return (
    <>
      <div className="p-6" data-testid="page-financials">
        <ViewOnlyBanner area="financials" />
        {initialPropertyId ? (
          <BackToPropertyLink propertyId={initialPropertyId} tab="financials" />
        ) : null}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold" data-testid="text-financials-title" data-tour="financials-heading">Financials</h1>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="financials" />
            <GuideButton pageKey="financials" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="financials" />

        <div className="flex border-b border-border mb-6 overflow-x-auto" data-tour="financials-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              aria-current={activeTab === tab.key ? "page" : undefined}
              className={`relative shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ${
                activeTab === tab.key ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => navigateTab(tab.key)}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-semibold rounded-full bg-blue-600 text-white px-1.5" data-testid={`badge-tab-${tab.key}`}>
                  {tab.badge > 99 ? "99+" : tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "balance" && gatesFailed && <div role="alert">Could not check Financials access. Please reload this page.</div>}
        {activeTab === "balance" && !gatesLoaded && <div role="status">Loading Balance…</div>}
        {activeTab === "balance" && gatesLoaded && !gatesFailed && !gateBlockForRoute(gatesByKey, TAB_ROUTES.balance) && pnlGate?.mode !== "hidden" && (
          pnlGate?.mode === "coming_soon"
            ? <ComingSoon message={pnlGate.message} testidSuffix="revenue-pnl" />
            : <BalanceTab />
        )}
        {activeTab === "pnl" && pnlGate?.mode !== "hidden" && (
          pnlGate?.mode === "coming_soon"
            ? <ComingSoon message={pnlGate.message} testidSuffix="revenue-pnl" />
            : <ProfitAndLoss properties={properties} initialPropertyId={initialPropertyId} />
        )}
        {activeTab === "expenses" && <ExpensesTab properties={properties} initialPropertyId={initialPropertyId} />}
        {activeTab === "income" && incomeGate?.mode !== "hidden" && (
          incomeGate?.mode === "coming_soon"
            ? <ComingSoon message={incomeGate.message} testidSuffix="revenue-income" />
            : <IncomeTab properties={properties} />
        )}
        {activeTab === "settings" && <SettingsTab properties={properties} />}
        {activeTab === "mail-inbox" && hasForwardedDocsBeta && (
          <ForwardedDocsTab properties={properties} />
        )}
        {activeTab === "statements" && showStatementsTab && statementsGate?.mode !== "hidden" && (
          statementsGate?.mode === "coming_soon"
            ? <ComingSoon message={statementsGate.message} testidSuffix="revenue-statements" />
            : <StatementsTab />
        )}
        {activeTab === "cards" && showCardsTab && cardsGate?.mode !== "hidden" && (
          cardsGate?.mode === "coming_soon"
            ? <ComingSoon message={cardsGate.message} testidSuffix="revenue-cards" />
            : <CardsTab />
        )}
      </div>
    </>
  );
}

